import React, { useEffect, useState } from 'react';
import { apiFetch, getStoredJwt } from '../lib/api';

const TABS = ['pending', 'published', 'rejected'];
const PRICES = ['$', '$$', '$$$', '$$$$'];
const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

const FIELDS = ['name', 'city', 'neighborhood', 'cuisine', 'description', 'instagram', 'twitter', 'website'];

export default function RestaurantCandidates() {
  const [tab, setTab] = useState('pending');
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [savingId, setSavingId] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = (t) => {
    setBusy(true); setErr('');
    apiFetch(`/api/admin/restaurants?status=${t}`)
      .then((list) => setItems(list || []))
      .catch((e) => setErr(e.message))
      .finally(() => setBusy(false));
  };

  useEffect(() => { load(tab); }, [tab]);

  const patchLocal = (id, patch) =>
    setItems((s) => s.map((x) => (x.id === id ? { ...x, ...patch } : x)));

  const save = async (item) => {
    setSavingId(item.id); setErr('');
    try {
      const payload = {};
      FIELDS.forEach((f) => { payload[f] = item[f] || ''; });
      payload.price = item.price || '$$';
      payload.tags = item.tags || [];
      await apiFetch(`/api/admin/restaurants/${item.id}`, { method: 'PATCH', body: payload });
    } catch (e) { setErr(e.message); }
    finally { setSavingId(null); }
  };

  const act = async (id, action) => {
    setErr('');
    try {
      await apiFetch(`/api/admin/restaurants/${id}/${action}`, { method: 'POST' });
      setItems((s) => s.filter((x) => x.id !== id));
    } catch (e) { setErr(e.message); }
  };

  const uploadImage = async (id, file) => {
    if (!file) return;
    setSavingId(id); setErr('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const jwt = getStoredJwt();
      const res = await fetch(`${BACKEND}/api/admin/restaurants/${id}/image`, {
        method: 'POST',
        credentials: 'include',
        headers: jwt ? { Authorization: `Bearer ${jwt}` } : {},
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Upload failed');
      patchLocal(id, { image_url: data.image_url });
    } catch (e) { setErr(e.message); }
    finally { setSavingId(null); }
  };

  const rehostImage = async (id) => {
    setSavingId(id); setErr('');
    try {
      const data = await apiFetch(`/api/admin/restaurants/${id}/rehost-image`, { method: 'POST' });
      patchLocal(id, { image_url: data.image_url });
    } catch (e) { setErr(e.message); }
    finally { setSavingId(null); }
  };

  const [bulkMsg, setBulkMsg] = useState('');
  const rehostAll = async () => {
    setBulkBusy(true); setErr(''); setBulkMsg('');
    try {
      const r = await apiFetch('/api/admin/restaurants/rehost-all-pending', { method: 'POST' });
      setBulkMsg(`Fetched images for ${r.updated} of ${r.total_pending} pending (${r.failed} couldn't be fetched — upload those manually).`);
      load(tab);
    } catch (e) { setErr(e.message); }
    finally { setBulkBusy(false); }
  };

  return (
    <div data-testid="restaurant-candidates">
      <div className="flex flex-wrap gap-3 mb-4 items-center">
        <div className="flex bg-white rounded-lg shadow-sm p-1">
          {TABS.map((t) => (
            <button
              key={t}
              data-testid={`candidate-tab-${t}`}
              onClick={() => setTab(t)}
              className={`px-4 py-2 text-sm font-semibold rounded-md ${tab === t ? 'bg-teal-600 text-white' : 'text-gray-600 hover:text-teal-600'}`}
            >{t.charAt(0).toUpperCase() + t.slice(1)}</button>
          ))}
        </div>
        {tab === 'pending' && (
          <button
            data-testid="candidate-rehost-all"
            onClick={rehostAll}
            disabled={bulkBusy}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-blue-700 disabled:opacity-50"
          >{bulkBusy ? 'Fetching images…' : '🔄 Fetch all images'}</button>
        )}
      </div>
      {bulkMsg && <div className="bg-blue-50 border border-blue-200 text-blue-800 p-3 rounded mb-3 text-sm" data-testid="candidate-bulk-msg">{bulkMsg}</div>}

      {err && <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded mb-3 text-sm">{err}</div>}
      {busy && <div className="text-gray-500 text-sm">Loading…</div>}
      {!busy && items.length === 0 && <div className="bg-white p-8 rounded-lg shadow-sm text-center text-gray-500">No {tab} restaurant candidates.</div>}

      <ul className="space-y-4">
        {items.map((s) => (
          <li key={s.id} data-testid={`candidate-row-${s.id}`} className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex flex-col md:flex-row gap-4">
              {/* Image preview + replace */}
              <div className="md:w-56 shrink-0">
                {s.image_url ? (
                  <img
                    src={s.image_url}
                    alt={s.name}
                    className="w-full h-36 object-cover rounded-lg border border-gray-200"
                    data-testid={`candidate-img-${s.id}`}
                    onError={(e) => { e.target.style.display = 'none'; if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex'; }}
                  />
                ) : null}
                <div
                  className="w-full h-36 rounded-lg border border-dashed border-gray-300 flex-col items-center justify-center text-xs text-gray-400 p-2 text-center"
                  style={{ display: s.image_url ? 'none' : 'flex' }}
                >
                  {(s.source_image_url || s.image_url) ? "Scraped image is blocked — fetch it or upload your own" : "No image"}
                </div>
                <label className="mt-2 block text-xs font-semibold text-teal-700 cursor-pointer hover:underline">
                  ⬆ Upload original image
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/gif"
                    className="hidden"
                    data-testid={`candidate-upload-${s.id}`}
                    onChange={(e) => uploadImage(s.id, e.target.files[0])}
                  />
                </label>
                {(s.source_image_url || s.image_url) && (
                  <button
                    type="button"
                    data-testid={`candidate-rehost-${s.id}`}
                    onClick={() => rehostImage(s.id)}
                    disabled={savingId === s.id}
                    className="mt-1 block text-xs font-semibold text-blue-600 hover:underline disabled:opacity-50"
                  >🔄 Fetch image from source</button>
                )}
                {s.source === 'scraper' && (
                  <div className="mt-1 text-[11px] text-gray-400">
                    {s.confidence != null && <>confidence {Math.round(s.confidence * 100)}% · </>}
                    {s.source_url && <a href={s.source_url} target="_blank" rel="noreferrer" className="hover:underline">source</a>}
                  </div>
                )}
              </div>

              {/* Editable fields */}
              <div className="flex-1 space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <input data-testid={`candidate-name-${s.id}`} value={s.name || ''} onChange={(e) => patchLocal(s.id, { name: e.target.value })} placeholder="Name" className="p-2 border border-gray-300 rounded text-sm font-semibold" />
                  <input value={s.city || ''} onChange={(e) => patchLocal(s.id, { city: e.target.value })} placeholder="City" className="p-2 border border-gray-300 rounded text-sm" />
                  <input value={s.neighborhood || ''} onChange={(e) => patchLocal(s.id, { neighborhood: e.target.value })} placeholder="Neighborhood" className="p-2 border border-gray-300 rounded text-sm" />
                  <input value={s.cuisine || ''} onChange={(e) => patchLocal(s.id, { cuisine: e.target.value })} placeholder="Cuisine" className="p-2 border border-gray-300 rounded text-sm" />
                </div>
                <textarea data-testid={`candidate-desc-${s.id}`} value={s.description || ''} onChange={(e) => patchLocal(s.id, { description: e.target.value })} placeholder="Description (rewrite to keep it original)" rows={3} className="w-full p-2 border border-gray-300 rounded text-sm" />
                <div className="grid grid-cols-3 gap-2">
                  <select value={s.price || '$$'} onChange={(e) => patchLocal(s.id, { price: e.target.value })} className="p-2 border border-gray-300 rounded text-sm">
                    {PRICES.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                  <input value={s.instagram || ''} onChange={(e) => patchLocal(s.id, { instagram: e.target.value })} placeholder="instagram" className="p-2 border border-gray-300 rounded text-sm" />
                  <input value={s.twitter || ''} onChange={(e) => patchLocal(s.id, { twitter: e.target.value })} placeholder="twitter/x" className="p-2 border border-gray-300 rounded text-sm" />
                </div>
                <input value={s.website || ''} onChange={(e) => patchLocal(s.id, { website: e.target.value })} placeholder="website" className="w-full p-2 border border-gray-300 rounded text-sm" />

                <div className="flex flex-wrap gap-2 pt-1">
                  <button data-testid={`candidate-save-${s.id}`} onClick={() => save(s)} disabled={savingId === s.id} className="bg-gray-800 text-white text-xs px-3 py-1.5 rounded hover:bg-gray-900 disabled:opacity-50">{savingId === s.id ? 'Saving…' : '💾 Save edits'}</button>
                  {tab !== 'published' && (
                    <button data-testid={`candidate-approve-${s.id}`} onClick={() => act(s.id, 'approve')} className="bg-teal-600 text-white text-xs px-3 py-1.5 rounded hover:bg-teal-700">✓ Approve → live</button>
                  )}
                  {tab !== 'rejected' && (
                    <button data-testid={`candidate-reject-${s.id}`} onClick={() => act(s.id, 'reject')} className="border border-gray-300 text-gray-700 text-xs px-3 py-1.5 rounded hover:bg-gray-50">✕ Reject</button>
                  )}
                  {s.source === 'scraper' && <span className="text-[11px] text-amber-600 self-center">scraped — rewrite copy before publishing</span>}
                </div>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
